import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { formatDay, today, type RosterRow, type Team } from '../lib/sports';
import { Button, Checkbox, Dialog, Field, Input, Select, useToast } from './ui';

interface Transfer {
  id: string;
  person_id: string;
  first_name: string;
  last_name: string;
  from_team_id: string;
  from_team_name: string | null;
  to_team_id: string;
  to_team_name: string | null;
  effective_date: string;
  note: string | null;
}

/**
 * Stammmannschaft einer Person ab einem Stichtag wechseln.
 *
 * Termine vor dem Stichtag bleiben bei der bisherigen Mannschaft - auch deren
 * Rückmeldungen und Anwesenheiten. Ab dem Stichtag ist die Person Teil der
 * neuen Mannschaft.
 */
export function TransferDialog({ row, team, teams, seasonId, onClose }: {
  row: RosterRow; team: Team; teams: Team[]; seasonId: string; onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [ziel, setZiel] = useState(teams[0]?.id ?? '');
  const [stichtag, setStichtag] = useState(today());
  const [nummer, setNummer] = useState(false);
  const [notiz, setNotiz] = useState('');

  const wechseln = useMutation({
    mutationFn: () => api.post<{ toTeamName: string; movedSessions: number }>(
      `/api/sports/teams/${team.id}/roster/${row.person_id}/transfer?seasonId=${seasonId}`,
      { toTeamId: ziel, effectiveDate: stichtag, keepJerseyNumber: nummer, note: notiz.trim() || null },
    ),
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ['sports-roster'] });
      await qc.invalidateQueries({ queryKey: ['sports-teams'] });
      await qc.invalidateQueries({ queryKey: ['sports-transfers'] });
      toast.show(`${row.first_name} ${row.last_name} wechselt ab ${formatDay(stichtag)} zur ${result.toTeamName}`);
      onClose();
    },
    onError: (error: unknown) => toast.show(error instanceof ApiError ? error.message : 'Wechsel nicht möglich', 'error'),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${row.first_name} ${row.last_name} wechseln`}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
        <Button loading={wechseln.isPending} disabled={!ziel || !stichtag} onClick={() => wechseln.mutate()}>Wechseln</Button>
      </>}
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Von <strong>{team.name}</strong>. Termine vor dem Stichtag bleiben bei {team.name}, samt Rückmeldungen und
          Anwesenheit. Ab dem Stichtag gehört die Person zum Kader der neuen Mannschaft.
        </p>
        <Field label="Neue Mannschaft" required>
          {(id) => (
            <Select id={id} value={ziel} onChange={(event) => setZiel(event.target.value)}>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          )}
        </Field>
        <Field label="Ab" required hint="Frühestens heute">
          {(id) => <Input id={id} type="date" min={today()} value={stichtag} onChange={(event) => setStichtag(event.target.value)} />}
        </Field>
        {row.jersey_number !== null && (
          <Checkbox label={`Rückennummer ${row.jersey_number} mitnehmen`} checked={nummer} onChange={setNummer} />
        )}
        <Field label="Notiz" hint="Erscheint im Verlauf">
          {(id) => <Input id={id} value={notiz} maxLength={200} placeholder="z. B. ab Rückrunde fix" onChange={(event) => setNotiz(event.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}

/** Wechsel in und aus dieser Mannschaft in der Saison. */
export function TransferHistory({ teamId, seasonId }: { teamId: string; seasonId: string }): JSX.Element | null {
  const verlauf = useQuery({
    queryKey: ['sports-transfers', teamId, seasonId],
    queryFn: () => api.get<Transfer[]>(`/api/sports/teams/${teamId}/roster-transfers`, { seasonId }),
  });
  if (!verlauf.data?.length) return null;
  return (
    <div className="mt-4 border-t border-slate-200 pt-3">
      <p className="mb-1 text-sm font-medium text-slate-700">Wechsel in dieser Saison</p>
      <ul className="space-y-1 text-sm">
        {verlauf.data.map((w) => {
          const zugang = w.to_team_id === teamId;
          return (
            <li key={w.id} className="text-slate-600">
              <span className={zugang ? 'text-emerald-700' : 'text-amber-700'}>{zugang ? '→' : '←'}</span>{' '}
              <strong className="font-medium text-slate-800">{w.first_name} {w.last_name}</strong>{' '}
              {zugang ? `von ${w.from_team_name ?? '?'}` : `zur ${w.to_team_name ?? '?'}`} ab {formatDay(w.effective_date)}
              {w.note ? <span className="text-slate-500"> · {w.note}</span> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
